/**
 * MapFactory - Factory for creating map provider instances
 *
 * Automatically detects which map API is available and creates the appropriate provider.
 */
import { GoogleMapProvider } from './GoogleMapProvider.js';
import { OSMMapProvider } from './OSMMapProvider.js';

export class MapFactory {
	/**
	 * Create a map provider instance
	 *
	 * @param {string} elementId - The DOM element ID for the map
	 * @param {Object} options - Map configuration options
	 * @param {string} options.preferredProvider - 'google', 'osm', or 'auto' (default)
	 * @returns {MapProvider|null} Map provider instance or null if no provider available
	 */
	static create(elementId, options = {}) {

		const provider = options.preferredProvider || MapFactory.detectProvider();

		switch (provider) {
			case 'google':
				if (MapFactory.isGoogleMapsAvailable()) {
					return new GoogleMapProvider(elementId, options);
				}
				if (MapFactory.isOSMAvailable()) {
					return new OSMMapProvider(elementId, options);
				}
				break;

			case 'osm':
				if (MapFactory.isOSMAvailable()) {
					return new OSMMapProvider(elementId, options);
				}
				if (MapFactory.isGoogleMapsAvailable()) {
					return new GoogleMapProvider(elementId, options);
				}
				break;

			case 'auto':
			default:
				// Try Google first, then OSM
				if (MapFactory.isGoogleMapsAvailable()) {
					return new GoogleMapProvider(elementId, options);
				}
				if (MapFactory.isOSMAvailable()) {
					return new OSMMapProvider(elementId, options);
				}
				break;
		}

		return null;
	}

	/**
	 * Detect which map provider is available
	 *
	 * @returns {string} 'google', 'osm', or 'none'
	 */
	static detectProvider() {

		// Check global gdSetMap setting first
		if (window.gdSetMap) {
			if ((window.gdSetMap === 'google' || window.gdSetMap === 'auto') && MapFactory.isGoogleMapsAvailable()) {
				return 'google';
			}
			if ((window.gdSetMap === 'osm' || window.gdSetMap === 'auto') && MapFactory.isOSMAvailable()) {
				return 'osm';
			}
		}

		// Auto-detect based on what's loaded
		if (MapFactory.isGoogleMapsAvailable()) {
			return 'google';
		}
		if (MapFactory.isOSMAvailable()) {
			return 'osm';
		}

		return 'none';
	}

	/**
	 * Check if Google Maps is available
	 *
	 * @returns {boolean}
	 */
	static isGoogleMapsAvailable() {
		return !!(window.google && typeof google.maps !== 'undefined');
	}

	/**
	 * Check if OSM/Leaflet is available
	 *
	 * @returns {boolean}
	 */
	static isOSMAvailable() {
		return !!(window.L && typeof L.version !== 'undefined');
	}

	/**
	 * Get the name of the currently available provider
	 *
	 * @returns {string} 'google', 'osm', or 'none'
	 */
	static getAvailableProvider() {
		return MapFactory.detectProvider();
	}
}
